import { FormEvent, useEffect, useMemo, useState } from 'react';
import { KeyIcon } from '@heroicons/react/24/outline';
import { useNavigate, useParams } from 'react-router-dom';

import { Alert, Button, Input, Spinner } from '../../components/ui';
import {
  useGetPublicWorkspaceV1ClientOpportunityRankerPublicWorkspacesPublicTokenGet,
  useRedeemPublicMagicLinkV1ClientOpportunityRankerPublicShareTokenRedeemPost,
  useSavePublicRevisionV1ClientOpportunityRankerPublicWorkspacesPublicTokenRevisionsPost,
} from '../../generated/client-opportunity-ranker/client-opportunity-ranker';
import {
  ComputedResults,
  PublicWorkspaceResponse,
  ScoringState,
} from './api';
import OpportunityMatrix from './OpportunityMatrix';
import OpportunityRankingTable from './OpportunityRankingTable';
import PublicRankerFilters from './PublicRankerFilters';
import {
  CompactOpportunityReadout,
  SaveRevisionBar,
  ScoringMethodology,
  TopTenCallout,
} from './PublicRankerContext';
import {
  applyFilters,
  computeRows,
  DEFAULT_FILTERS,
  OpportunityStatus,
  normalizeThemes,
  updateScore,
  updateStatus,
} from './publicScoring';

export default function ClientOpportunityRankerSharePage() {
  const { publicToken } = useParams<{ publicToken: string }>();
  const navigate = useNavigate();
  const [email, setEmail] = useState('');
  const [accessCode, setAccessCode] = useState('');
  const [draftState, setDraftState] = useState<ScoringState | null>(null);
  const [savedState, setSavedState] = useState<ScoringState | null>(null);
  const [savedResults, setSavedResults] = useState<ComputedResults | undefined>();
  const [baseRevisionNumber, setBaseRevisionNumber] = useState(0);
  const [stateScope, setStateScope] = useState<'canonical' | 'viewer'>('canonical');
  const [filters, setFilters] = useState(DEFAULT_FILTERS);
  const [expandedId, setExpandedId] = useState<string | null>(null);
  const [scoreEditedIds, setScoreEditedIds] = useState<Set<string>>(() => new Set());

  useEffect(() => {
    const root = document.documentElement;
    const hadDarkMode = root.classList.contains('dark');
    const previousColorScheme = root.style.colorScheme;
    const forceLightMode = () => {
      if (root.classList.contains('dark')) {
        root.classList.remove('dark');
      }
      root.style.colorScheme = 'light';
    };

    forceLightMode();
    const observer = new MutationObserver(forceLightMode);
    observer.observe(root, { attributes: true, attributeFilter: ['class'] });

    return () => {
      observer.disconnect();
      root.classList.toggle('dark', hadDarkMode);
      root.style.colorScheme = previousColorScheme;
    };
  }, []);

  const workspaceQuery =
    useGetPublicWorkspaceV1ClientOpportunityRankerPublicWorkspacesPublicTokenGet<PublicWorkspaceResponse>(
      publicToken ?? '',
      {
        query: {
          queryKey: ['client-opportunity-ranker-public', publicToken],
          enabled: Boolean(publicToken),
          retry: false,
        },
      }
    );

  const redeemMutation = useRedeemPublicMagicLinkV1ClientOpportunityRankerPublicShareTokenRedeemPost({
    mutation: {
      onSuccess: (data) => {
        navigate(data.redirect_path, { replace: true });
        workspaceQuery.refetch();
      },
    },
  });

  const saveMutation = useSavePublicRevisionV1ClientOpportunityRankerPublicWorkspacesPublicTokenRevisionsPost({
    mutation: {
      onSuccess: (revision) => {
        const scoringState = revision.scoring_state as ScoringState;
        setDraftState(scoringState);
        setSavedState(scoringState);
        setSavedResults(revision.computed_results as ComputedResults);
        setBaseRevisionNumber(revision.revision_number);
        setStateScope((revision.state_scope ?? 'viewer') as 'canonical' | 'viewer');
        setScoreEditedIds(new Set());
        workspaceQuery.refetch();
      },
    },
  });

  useEffect(() => {
    const revision = workspaceQuery.data?.revision;
    if (!revision) return;
    setDraftState(revision.scoring_state);
    setSavedState(revision.scoring_state);
    setSavedResults(revision.computed_results);
    setBaseRevisionNumber(revision.revision_number);
    setStateScope((revision.state_scope ?? 'canonical') as 'canonical' | 'viewer');
    setScoreEditedIds(new Set());
  }, [workspaceQuery.data?.revision]);

  const handleRedeem = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    if (!publicToken) return;
    redeemMutation.mutate({
      token: publicToken,
      data: { viewer_email: email, access_code: accessCode.trim() || undefined },
    });
  };

  const rows = useMemo(
    () => (draftState ? computeRows(draftState, savedResults, { recomputeIds: scoreEditedIds }) : []),
    [draftState, savedResults, scoreEditedIds]
  );
  const visibleRows = useMemo(() => {
    const filteredRows = applyFilters(rows, filters);
    if (!expandedId || filteredRows.some((row) => row.id === expandedId)) return filteredRows;
    const expandedRow = rows.find((row) => row.id === expandedId);
    return expandedRow ? [expandedRow, ...filteredRows] : filteredRows;
  }, [expandedId, rows, filters]);
  const themes = useMemo(() => normalizeThemes(draftState?.themes), [draftState?.themes]);
  const sources = useMemo(
    () => Array.from(new Set(rows.map((row) => row.source).filter(Boolean) as string[])).sort(),
    [rows]
  );
  const hasChanges = Boolean(draftState && savedState && JSON.stringify(draftState) !== JSON.stringify(savedState));
  const accessRequired =
    !workspaceQuery.isLoading &&
    ((workspaceQuery.error as { response?: { status?: number } } | null)?.response?.status === 401 ||
      (!workspaceQuery.data && !workspaceQuery.isError));

  const updateFactorScore = (id: string, kind: 'gain' | 'pain', index: number, value: number) => {
    setScoreEditedIds((current) => new Set(current).add(id));
    setDraftState((current) => (current ? updateScore(current, id, kind, index, value) : current));
  };

  const updateOpportunityStatus = (id: string, status: OpportunityStatus) => {
    setDraftState((current) => (current ? updateStatus(current, id, status) : current));
  };

  const updateDetailField = (id: string, label: string, value: string) => {
    setDraftState((current) => {
      if (!current) return current;
      const details = current.details ?? {};
      const detail = details[id] ?? {};
      const fields = detail.fields ?? [];
      const hasField = fields.some(([fieldLabel]) => fieldLabel === label);
      const nextFields = hasField
        ? fields.map(([fieldLabel, fieldValue]) => [fieldLabel, fieldLabel === label ? value : fieldValue] as [string, string])
        : [...fields, [label, value] as [string, string]];
      return {
        ...current,
        details: {
          ...details,
          [id]: {
            ...detail,
            fields: nextFields,
          },
        },
      };
    });
  };

  const expandRow = (id: string) => {
    setExpandedId((current) => (current === id ? null : id));
    window.setTimeout(() => document.getElementById(`opportunity-${id}`)?.scrollIntoView({ block: 'center' }), 0);
  };

  if (workspaceQuery.isLoading) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 dark:bg-dark-bg">
        <Spinner size="lg" />
      </div>
    );
  }

  if (accessRequired || redeemMutation.isError) {
    return (
      <AccessGate
        email={email}
        accessCode={accessCode}
        isPending={redeemMutation.isPending}
        isError={redeemMutation.isError}
        onEmailChange={setEmail}
        onAccessCodeChange={setAccessCode}
        onSubmit={handleRedeem}
      />
    );
  }

  if (workspaceQuery.isError || !workspaceQuery.data || !draftState) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6 dark:bg-dark-bg">
        <Alert variant="error">This opportunity workspace is unavailable.</Alert>
      </div>
    );
  }

  return (
    <main
      aria-label="Opportunity Ranker workspace"
      className="h-screen overflow-y-auto bg-white text-charcoal dark:bg-dark-bg dark:text-gray-100"
    >
      <Hero
        title={workspaceQuery.data.workspace.title}
        opportunityCount={rows.length}
        visibleCount={visibleRows.length}
        themeCount={themes.length}
        revisionNumber={baseRevisionNumber}
        stateScope={stateScope}
      />
      <OpportunityMatrix rows={visibleRows} selectedId={expandedId} onSelect={expandRow} />
      <CompactOpportunityReadout rows={visibleRows} totalCount={rows.length} onSelect={expandRow} />
      <ScoringMethodology state={draftState} />
      <TopTenCallout rows={rows} />
      <PublicRankerFilters
        filters={filters}
        themes={themes}
        sources={sources}
        visibleCount={visibleRows.length}
        totalCount={rows.length}
        onChange={setFilters}
      />
      <OpportunityRankingTable
        rows={visibleRows}
        state={draftState}
        expandedId={expandedId}
        onToggle={expandRow}
        onScoreChange={updateFactorScore}
        onStatusChange={updateOpportunityStatus}
        onDetailFieldChange={updateDetailField}
      />
      <SaveRevisionBar
        hasChanges={hasChanges}
        isSaving={saveMutation.isPending}
        saveError={saveMutation.isError ? saveErrorText(saveMutation.error) : null}
        onSave={() =>
          draftState &&
          publicToken &&
          saveMutation.mutate({
            publicToken,
            data: {
              base_revision_number: baseRevisionNumber,
              scoring_state: draftState,
            },
          })
        }
      />
    </main>
  );
}

function AccessGate({
  email,
  accessCode,
  isPending,
  isError,
  onEmailChange,
  onAccessCodeChange,
  onSubmit,
}: {
  email: string;
  accessCode: string;
  isPending: boolean;
  isError: boolean;
  onEmailChange: (email: string) => void;
  onAccessCodeChange: (accessCode: string) => void;
  onSubmit: (event: FormEvent<HTMLFormElement>) => void;
}) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-gray-50 p-6 dark:bg-dark-bg">
      <form
        className="w-full max-w-md rounded-lg border border-gray-200 bg-white p-6 dark:border-dark-border dark:bg-dark-surface"
        onSubmit={onSubmit}
      >
        <h1 className="font-title text-h1 text-charcoal dark:text-gray-100">Opportunity Ranker</h1>
        <p className="mt-2 text-small text-gray-500 dark:text-gray-400">
          Enter your work email and the access code if one was provided.
        </p>
        {isError && (
          <Alert variant="error" className="mt-4">
            This share link is invalid, expired, revoked, or the email/code combination is not allowed.
          </Alert>
        )}
        <label className="mt-5 block text-small font-medium text-charcoal dark:text-gray-100">
          Email address
          <Input
            className="mt-2"
            value={email}
            onChange={(event) => onEmailChange(event.target.value)}
            type="email"
            autoComplete="email"
            required
          />
        </label>
        <label className="mt-4 block text-small font-medium text-charcoal dark:text-gray-100">
          Access code
          <Input
            className="mt-2 font-mono uppercase"
            value={accessCode}
            onChange={(event) => onAccessCodeChange(event.target.value)}
            type="text"
            autoComplete="one-time-code"
            placeholder="Q7KD-M9RA-42VT"
          />
        </label>
        <Button type="submit" disabled={!email || isPending} className="mt-5 w-full justify-center">
          {isPending ? <Spinner size="sm" /> : <KeyIcon className="h-4 w-4" />}
          Open Workspace
        </Button>
      </form>
    </div>
  );
}

function Hero({
  title,
  opportunityCount,
  visibleCount,
  themeCount,
  revisionNumber,
  stateScope,
}: {
  title: string;
  opportunityCount: number;
  visibleCount: number;
  themeCount: number;
  revisionNumber: number;
  stateScope: 'canonical' | 'viewer';
}) {
  const eyebrow = opportunityEyebrow(title);

  return (
    <section className="bg-white px-4 pb-7 pt-8 dark:bg-dark-surface">
      <div className="mx-auto max-w-7xl">
        <p className="text-micro font-semibold uppercase tracking-wide text-eliza-red">{eyebrow}</p>
        <h1 className="mt-2 font-title text-display text-charcoal dark:text-gray-100">{title}</h1>
        <p className="mt-3 max-w-5xl text-body leading-7 text-gray-600 dark:text-gray-300">
          All opportunities are scored on a single framework across workshop notes, THL portfolio components, and deferred
          ideas. This is a starting point for leadership conversation, not a final ranking.
        </p>
        <div className="mt-6 grid gap-3 sm:grid-cols-4">
          <Metric label="Opportunities" value={opportunityCount} />
          <Metric label="Visible now" value={visibleCount} />
          <Metric label="Themes" value={themeCount} />
          <Metric label={stateScope === 'viewer' ? 'Your view' : 'Source revision'} value={revisionNumber} />
        </div>
      </div>
    </section>
  );
}

function opportunityEyebrow(title: string): string {
  const accountName = title
    .replace(/\s+(AI\s+)?Opportunity Ranking$/i, '')
    .replace(/\s+Value Creation Ranking$/i, '')
    .replace(/\s+Ranking$/i, '')
    .trim();

  return accountName ? `${accountName} AI Opportunity Ranking` : 'AI Opportunity Ranking';
}

function Metric({ label, value }: { label: string; value: number }) {
  return (
    <div className="rounded-lg border border-gray-200 bg-gray-50 p-4 dark:border-dark-border dark:bg-dark-surface-2">
      <div className="text-micro font-semibold uppercase text-gray-500 dark:text-gray-400">{label}</div>
      <div className="mt-1 text-h2 font-semibold text-charcoal dark:text-gray-100">{value}</div>
    </div>
  );
}

function saveErrorText(error: unknown) {
  const status = (error as { response?: { status?: number } })?.response?.status;
  if (status === 409) return 'Your saved view has a newer version. Refresh before saving again.';
  if (status === 401) return 'Your share session expired or was revoked. Reopen the magic link to continue.';
  return 'Unable to save your view. Please try again.';
}
